import InfiniteScroll from 'react-infinite-scroll-component';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { ReactNode, useState } from 'react';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerAddUpdateAssetModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerAddUpdateAssetModal.tsx';
import { IssuerPortfolioTransactionFullInfoModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerPortfolioTransactionFullInfoModal.tsx';
import { PortfolioTransactionTypeBlock } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioTransactionsTab.tsx';
import {
  useIssuerDeletePortfolioAssetMutation,
  useIssuerPortfolioAssetByIdQuery,
  useIssuerPortfolioTransactionsInfiniteListQuery,
  useIssuerRefreshAssetPriceMutation,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { BaseModalProps, Button, ConfirmModal, ModalContentWrapper, Spinner } from 'ui';
import { goodFormatNumber, goodFormatNumberCustom } from 'ui/utils';
import {
  AssetType,
  PortfolioTransactionExtended,
  PortfolioTransactionStatus,
  PortfolioTransactionType,
} from 'api';

export const InfoItem = ({ title, children }: { title: string; children: ReactNode }) => {
  return (
    <div className="w-full flex text-sm items-center gap-4 py-3 border-b">
      <div className="text-gray-500 min-w-[33%]">{title}</div>
      {children}
    </div>
  );
};

interface IssuerPortfolioAssetFullInfoModalProps extends BaseModalProps {
  id: number;
  name: string;
  portfolioId: number;
}

export const IssuerPortfolioAssetFullInfoModal = ({
  id,
  portfolioId,
  name,
  open,
  onClose,
}: IssuerPortfolioAssetFullInfoModalProps) => {
  const { data: dataAsset, isLoading } = useIssuerPortfolioAssetByIdQuery({ assetId: id });
  const { mutateAsync: mutateAsyncDelete } = useIssuerDeletePortfolioAssetMutation();
  const { mutateAsync: mutateRefreshPrice, isLoading: isRefreshing } =
    useIssuerRefreshAssetPriceMutation();
  const [openEdit, setOpenEdit] = useState(false);
  const [openDel, setOpenDel] = useState(false);

  const PRICE_AUTO_UPDATE_TYPES: AssetType[] = [
    AssetType.ListedSharesAU,
    AssetType.ListedSharesOverseas,
    AssetType.ETF,
    AssetType.ListedFunds,
  ];

  const showNextUpdate =
    !!dataAsset?.asset?.asset_type && PRICE_AUTO_UPDATE_TYPES.includes(dataAsset.asset.asset_type);

  return (
    <ModalContentWrapper
      variant="right"
      title={name}
      open={open}
      onClose={onClose}
      onEdit={
        !isLoading && dataAsset?.asset
          ? () => {
              setOpenEdit(true);
            }
          : undefined
      }
      onDelete={
        !isLoading && dataAsset?.asset
          ? () => {
              setOpenDel(true);
            }
          : undefined
      }
    >
      {isLoading && (
        <div className="w-full h-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      )}
      {!isLoading && dataAsset?.asset && (
        <>
          <div className="flex flex-col justify-between gap-4 h-full">
            <div className="flex flex-col border-t">
              <InfoItem title="Type">
                <div>
                  {dataAsset?.asset?.asset_type || dataAsset?.asset?.currency?.toUpperCase() || '-'}
                </div>
              </InfoItem>
              {dataAsset?.asset?.industry && (
                <InfoItem title="Industry">
                  <div>{dataAsset.asset.industry}</div>
                </InfoItem>
              )}
              <InfoItem title="Price per share">
                <div>
                  {goodFormatNumberCustom(
                    dataAsset?.asset?.price_per_share || 0,
                    dataAsset?.asset?.currency?.toUpperCase(),
                  )}
                </div>
              </InfoItem>
              <InfoItem title="Last price updated">
                <div>
                  {dateToPrint(dataAsset?.asset?.price_updated_at, DateVariant.Information, false)}
                </div>
              </InfoItem>
              {showNextUpdate && (
                <InfoItem title="Next price update">
                  <div className="flex flex-1 items-center justify-between">
                    <span className="whitespace-nowrap">
                      {dayjs().add(1, 'month').startOf('month').format('DD MMM YYYY')}
                    </span>
                    <Button
                      variant="secondary"
                      onClick={() => void mutateRefreshPrice(id)}
                      className="w-fit text-xs py-1 px-2"
                      disabled={isRefreshing}
                    >
                      Update now
                    </Button>
                  </div>
                </InfoItem>
              )}
              <InfoItem title="Currency">
                <div>{dataAsset?.asset?.currency?.toUpperCase()}</div>
              </InfoItem>
              <InfoItem title="Quantity">
                <div>{goodFormatNumber(dataAsset?.asset?.quantity || 0)}</div>
              </InfoItem>
              <InfoItem title="Total asset value">
                <div>
                  {goodFormatNumberCustom(
                    dataAsset?.asset?.total_asset_value || 0,
                    dataAsset?.asset?.currency?.toUpperCase(),
                  )}
                </div>
              </InfoItem>
              <InfoItem title="% of portfolio value">
                <div>{goodFormatNumber(dataAsset?.asset?.percent_portfolio_value || 0)}%</div>
              </InfoItem>
              <InfoItem title="PNL">
                <div>
                  <div
                    className={classNames('whitespace-nowrap text-nowrap', {
                      'text-green-600': (dataAsset?.asset?.pnl || 0) > 0,
                      'text-red-600': (dataAsset?.asset?.pnl || 0) < 0,
                    })}
                  >
                    <div>
                      {goodFormatNumberCustom(
                        dataAsset?.asset?.pnl ? +dataAsset?.asset?.pnl : 0,
                        dataAsset?.asset?.currency?.toUpperCase(),
                      )}
                    </div>
                  </div>
                </div>
              </InfoItem>
              <InfoItem title="PNL Percent">
                <div>
                  <div
                    className={classNames('whitespace-nowrap text-nowrap', {
                      'text-green-600': (dataAsset?.asset?.pnl_ratio || 0) > 0,
                      'text-red-600': (dataAsset?.asset?.pnl_ratio || 0) < 0,
                    })}
                  >
                    <div>
                      {(dataAsset?.asset?.pnl_ratio || 0) <= 0 ? '' : '+'}
                      {goodFormatNumber(
                        dataAsset?.asset?.pnl_ratio ? +dataAsset?.asset?.pnl_ratio : 0,
                      )}
                      %
                    </div>
                  </div>
                </div>
              </InfoItem>

              <AssetTransactions assetId={id} portfolioId={portfolioId} />
            </div>
            <div className="flex flex-col gap-4 w-full"></div>
          </div>
          <IssuerAddUpdateAssetModal
            portfolioId={portfolioId}
            onClose={() => setOpenEdit(false)}
            open={openEdit}
            item={dataAsset?.asset}
          />
          <ConfirmModal
            title={<span>Delete asset {name}</span>}
            onClose={() => setOpenDel(false)}
            open={openDel}
            onConfirm={async () => {
              await mutateAsyncDelete(id);
              onClose();
            }}
          />
        </>
      )}
    </ModalContentWrapper>
  );
};

export interface AssetTransactionsProps {
  assetId: number;
  portfolioId: number;
}

export const AssetTransactions = ({ assetId, portfolioId }: AssetTransactionsProps) => {
  const {
    fetchNextPage,
    isLoading,
    hasNextPage,
    data: dataList,
  } = useIssuerPortfolioTransactionsInfiniteListQuery({
    page: 1,
    page_size: 15,
    asset_id: assetId,
    portfolio_id: portfolioId,
  });

  const transactions = (dataList?.pages || []).flatMap(({ data }) => data);

  return (
    <>
      <div className="text-[16px] leading-[22px] font-[500] pt-6 pb-4 border-b border-b-gray-200">
        Transactions
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-32 w-full">
          <Spinner size="md" variant="primary" />
        </div>
      ) : (
        <InfiniteScroll
          scrollableTarget="modal_scroll"
          next={fetchNextPage}
          dataLength={transactions?.length ?? 0}
          hasMore={hasNextPage ?? false}
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
          style={{ overflow: 'hidden!important' }}
        >
          {transactions?.map((item, i) => (
            <AssetPortfolioTransactionItem item={item} key={`transaction_${i}`} />
          ))}
        </InfiniteScroll>
      )}
    </>
  );
};

export interface AssetPortfolioTransactionItemProps {
  item: PortfolioTransactionExtended;
}

export const AssetPortfolioTransactionItem = ({ item }: AssetPortfolioTransactionItemProps) => {
  const [openFull, setOpenFull] = useState(false);
  const {
    transaction_type,
    status,
    quantity,
    confirmation_date,
    from_bank,
    asset,
    to_bank,
    amount,
    price_per_share,
  } = item;

  const currency = (
    asset?.currency ||
    from_bank?.bank_currency ||
    to_bank?.bank_currency
  )?.toUpperCase();

  const isDividendReinvest =
    transaction_type === PortfolioTransactionType.IncomeDividendReinvestment;
  const isFee = transaction_type === PortfolioTransactionType.Fee;
  const isRevaluation = transaction_type === PortfolioTransactionType.Revaluation;
  const isSplit = transaction_type === PortfolioTransactionType.Split;

  return (
    <>
      <div
        className="flex w-full gap-4 justify-between py-2 border-b border-b-gray-200 hover:bg-gray-50 cursor-pointer"
        onClick={() => setOpenFull(true)}
      >
        <div className="flex flex-col gap-1">
          <div>
            <PortfolioTransactionTypeBlock
              type={transaction_type}
              isSettle={status === PortfolioTransactionStatus.Completed}
              additionalInfo={isSplit ? (quantity < 0 ? 'Reverse' : 'Forward') : undefined}
            />
          </div>
          <div className="text-gray-500 text-sm">
            {dateToPrint(confirmation_date, DateVariant.Table, false)}
          </div>
        </div>

        <div className="flex flex-col gap-1 text-right">
          <div className="whitespace-nowrap text-nowrap">
            {isRevaluation || isSplit || isDividendReinvest ? (
              <>-</>
            ) : amount ? (
              <>
                {isFee ? '-' : '+'}
                {goodFormatNumberCustom(amount, currency, 0, 2)}
              </>
            ) : (
              <>
                {to_bank ? '+' : '-'}
                {goodFormatNumberCustom(price_per_share * quantity, currency, 0, 2)}
              </>
            )}
          </div>

          <div className="whitespace-nowrap text-nowrap">
            {isRevaluation ? (
              <>{price_per_share ? goodFormatNumberCustom(price_per_share, currency, 0, 2) : '-'}</>
            ) : amount ? (
              <>-</>
            ) : (
              <>
                {from_bank || isDividendReinvest || (isSplit && quantity >= 0)
                  ? '+'
                  : isSplit
                  ? ''
                  : '-'}
                {quantity || 0}
              </>
            )}
          </div>
        </div>
      </div>
      {openFull && (
        <IssuerPortfolioTransactionFullInfoModal
          id={item.id}
          portfolioId={item.portfolio_id}
          open={openFull}
          onClose={() => setOpenFull(false)}
        />
      )}
    </>
  );
};
